"use client";

import type { ArticleListingPageStoryblok } from "~/generated/sb/blok-types";
import "./article-listing.css";
import type { BlokProps } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import { useMemo } from "react";
import { Card } from "~/components/bloks/ArticleCardBlok/ArticleCardBlok";
import { ClientArticleGrid } from "~/components/bloks/ArticleListingPageBlok/ClientArticleGrid";
import { BlokWrap } from "~/components/bloks/BlokWrap";
import { Link } from "~/i18n/routing";
import type { BlokData } from "~/srpc/routes/story.router";
import styles from "./ArticleListingPageBlok.module.css";

type TabData = {
	text: string;
	slug: string;
};

const Tabs = ({ tabs, current }: { tabs: TabData[]; current: TabData }) => {
	return (
		<nav
			className={clsx(styles.heading__wrapper, (!tabs || tabs.length < 1) && styles["no-border"])}
		>
			{!tabs?.length && <h1>{current.text}</h1>}
			{tabs?.map((target, key) => {
				if (target.slug === current.slug) return <h1 key={key}>{target.text}</h1>;

				const slug = target.slug === "artikler" ? "" : target.slug;
				return (
					<Link href={`/artikler/${slug}`} key={key}>
						{target.text}
					</Link>
				);
			})}
		</nav>
	);
};

const FeaturedArticle = ({
	featuredArticle,
	preview,
}: {
	featuredArticle: BlokData["featuredArticle"]["featuredArticle"];
	preview: boolean;
}) => {
	return (
		<section
			className={clsx(styles.featured__article, "alp__featured")}
			data-layout="page"
			data-no-padding-bottom="true"
			data-container="small"
		>
			{featuredArticle && (
				<Card article={featuredArticle as BlokData["articleCard"]} preview={preview} />
			)}
		</section>
	);
};

export const ArticleListingPageBlok = ({
	blok,
	slug,
	preview,
	data,
	listings,
}: BlokProps<ArticleListingPageStoryblok, BlokData["featuredArticle"]> & {
	listings?: BlokData["featuredArticle"]["articles"];
	slug?: string;
}) => {
	const tabs = useMemo(() => {
		return listings?.map((listing) => ({
			slug: listing.slug,
			text: listing.content.Title || listing.name,
		}));
	}, [listings]);

	return (
		<BlokWrap editable={blok}>
			<section className={styles.root} data-layout="page" data-no-padding-bottom="true">
				<Tabs
					tabs={tabs ?? []}
					current={{
						text: blok.title,
						slug: slug ?? "artikler",
					}}
				/>
				<p className={styles.description}>{blok.description}</p>
			</section>
			<FeaturedArticle featuredArticle={data?.featuredArticle} preview={preview} />
			<ClientArticleGrid articles={data?.articles} preview={preview} />
		</BlokWrap>
	);
};
